import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { t } from 'i18n/runtime';
import {
  getConsent,
  setConsent,
  subscribe,
  clearAnalyticsCookies,
  clearMarketingCookies,
  OPEN_SETTINGS_EVENT,
} from './consentStore';
import { applyConsent, initConsentMode } from './loadAnalytics';
import './cookie-banner.css';

export { OPEN_SETTINGS_EVENT };

const PUBLIC = process.env.PUBLIC_URL || '';

// Reject has to be exactly as reachable as Accept — same level, same visual
// weight, one click. A banner where refusing takes an extra step through a
// settings panel is the single most-cited defect in EU cookie-banner
// enforcement, so the two primary buttons are deliberately identical in size
// and the panel is an optional third path, not the only route to "no".

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const firstButton = useRef(null);

  // Declare the denied defaults immediately, before anything can load a tag.
  useEffect(() => {
    initConsentMode();
    const consent = getConsent();
    applyConsent(consent);
    // Anyone who visited before consent gating existed still carries _ga cookies
    // set without consent, and a returning visitor with no stored decision would
    // otherwise keep them indefinitely. If analytics is not affirmatively
    // granted right now, those cookies have no basis to exist — clear them.
    if (!consent.analytics) clearAnalyticsCookies();
    if (!consent.marketing) clearMarketingCookies();
    setAnalytics(consent.analytics);
    setMarketing(consent.marketing);
    setVisible(!consent.decided);
  }, []);

  useEffect(() => subscribe((c) => applyConsent(c)), []);

  // The footer "Cookie settings" link reopens the panel from anywhere.
  useEffect(() => {
    const open = () => {
      const consent = getConsent();
      setAnalytics(consent.analytics);
      setMarketing(consent.marketing);
      setPanelOpen(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, open);
  }, []);

  useEffect(() => {
    if (visible && firstButton.current) firstButton.current.focus();
  }, [visible]);

  const commit = useCallback((next) => {
    const previous = getConsent();
    setConsent(next);
    setVisible(false);
    setPanelOpen(false);
    // Scripts already in the page cannot be un-injected. When a visitor
    // withdraws a consent they had previously given, the cookies are cleared by
    // the store and a reload guarantees nothing is left running.
    if (previous.analytics && !next.analytics) window.location.reload();
  }, []);

  if (!visible) return null;

  return (
    <div
      className="ti-consent"
      role="dialog"
      aria-modal="false"
      aria-labelledby="ti-consent-title"
    >
      <div className="ti-consent__inner">
        <div className="ti-consent__copy">
          <p className="ti-consent__title" id="ti-consent-title">
            {t('We ask before we track')}
          </p>
          <p className="ti-consent__text">
            {t(
              'We use cookies that are strictly necessary to run this site. With your consent we would also like to measure how the site is used and show you our email sign-up at sensible moments. You can change your mind at any time.'
            )}{' '}
            <Link className="ti-consent__link" to={`${PUBLIC}/cookie-policy`}>
              {t('Cookie Policy')}
            </Link>
            {' · '}
            <Link className="ti-consent__link" to={`${PUBLIC}/privacy-policy`}>
              {t('Privacy Policy')}
            </Link>
          </p>
        </div>

        {panelOpen && (
          <div className="ti-consent__panel">
            <div className="ti-consent__row">
              <div>
                <p className="ti-consent__rowTitle">{t('Strictly necessary')}</p>
                <p className="ti-consent__rowText">
                  {t('Required to run the site and to remember this choice. Always on.')}
                </p>
              </div>
              <span className="ti-consent__locked">{t('Always on')}</span>
            </div>

            <label className="ti-consent__row">
              <div>
                <p className="ti-consent__rowTitle">{t('Analytics')}</p>
                <p className="ti-consent__rowText">
                  {t('Aggregate measurement of which pages are used. No analytics script loads without this.')}
                </p>
              </div>
              <input
                type="checkbox"
                className="ti-consent__toggle"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
              />
            </label>

            <label className="ti-consent__row">
              <div>
                <p className="ti-consent__rowTitle">{t('Marketing')}</p>
                <p className="ti-consent__rowText">
                  {t('Lets us show our email sign-up without repeating it to the same person.')}
                </p>
              </div>
              <input
                type="checkbox"
                className="ti-consent__toggle"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
              />
            </label>
          </div>
        )}

        <div className="ti-consent__actions">
          <button
            type="button"
            ref={firstButton}
            className="ti-consent__btn ti-consent__btn--primary"
            onClick={() => commit({ analytics: true, marketing: true })}
          >
            {t('Accept all')}
          </button>
          <button
            type="button"
            className="ti-consent__btn ti-consent__btn--primary"
            onClick={() => commit({ analytics: false, marketing: false })}
          >
            {t('Reject all')}
          </button>
          {panelOpen ? (
            <button
              type="button"
              className="ti-consent__btn ti-consent__btn--ghost"
              onClick={() => commit({ analytics, marketing })}
            >
              {t('Save choices')}
            </button>
          ) : (
            <button
              type="button"
              className="ti-consent__btn ti-consent__btn--ghost"
              onClick={() => setPanelOpen(true)}
            >
              {t('Manage')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
